/**
 * @fileoverview Hook UI Layer especializado para gestionar el modo de chat ('chat' | 'notes') en InboxScreenMC.
 * Permite alternar y controlar el modo de chat de forma centralizada, reactiva y desacoplada del resto del UI state.
 *
 * @module useInboxChatModeUI
 * @category Hook - UI Layer
 * @since 3.2.1
 * @version 3.2.2
 *
 * @typedef {Object} UseInboxChatModeUIResult
 * @property {('chat'|'notes')} selectedChatMode - Modo de chat actual ('chat' para mensajes, 'notes' para notas internas)
 * @property {function(mode: 'chat'|'notes'):void} handleToggleChatMode - Handler para cambiar el modo de chat
 *
 * @function useInboxChatModeUI
 * @description
 * **Arquitectura Optimizada (UI Layer):**
 * - Solo gestiona el estado de modo de chat, sin mezclar lógica de negocio ni UI global
 * - Permite alternar modo de chat desde cualquier componente descendiente
 * - Implementa useCallback para estabilidad referencial
 * - Cumple separación estricta de capas y dominio
 *
 * @returns {UseInboxChatModeUIResult} Estado y handler del modo de chat
 *
 * @example
 * // Uso básico en InboxScreenMC
 * const { selectedChatMode, handleToggleChatMode } = useInboxChatModeUI();
 *
 * // Cambiar a modo notas:
 * handleToggleChatMode('notes');
 *
 * // Cambiar a modo chat:
 * handleToggleChatMode('chat');
 *
 * @see {@link InboxScreenMC} Uso en pantalla principal de Inbox
 */
import { useState, useCallback, useEffect } from 'react';
import { sharingInformationService } from '../../../../services/sharingInformationService';

function useInboxChatModeUI() {
  const [selectedChatMode, setSelectedChatMode] = useState('chat');

  const handleToggleChatMode = useCallback((mode) => {
    setSelectedChatMode(mode);
  }, []);

  useEffect(() => {
    const subscription$ = sharingInformationService.getSubject();
    const myObservable = subscription$.subscribe((data) => {
      if (data.handleToggleChatMode) {
        handleToggleChatMode(data.mode);
      }
    });
    return () => {
      myObservable.unsubscribe();
    };
  }, []);

  return { selectedChatMode, handleToggleChatMode };
}

export default useInboxChatModeUI;
